import { optionalOutcome, participationOutcome } from './grading-mode.js';
import { matchedKeywordOption } from './keyword.js';
import { resolveMatchedBranch, activeFeedbackForBranch } from './dropdown.js';
import { pickScenarioBranch, resolveScenarioRoles } from './scenario.js';
import { addVersionWithRoles, applyVersionHead, pickVersion, removeVersionWith, versionRoles, writeVersionWithRoles } from './versions.js';
const TRIGGER_INFIX = '_word_';
/** Builds the trigger string for a specific word within a specific blank — scoped one level
 * deeper than Keyword's own `option_${id}` convention, since FITB has multiple independent
 * blanks rather than one answer field. */
export function buildFitbWordTrigger(blankId, wordId) {
    return `blank_${blankId}${TRIGGER_INFIX}${wordId}`;
}
/**
 * The sentinel `wordId` for "this blank was left empty", in OPTIONAL only.
 *
 * FITB is excluded from the block-level `no_response` because its blank is a SPECTRUM — graded
 * already distinguishes "nothing anywhere" (`unanswered`) from "some blanks filled"
 * (`incomplete_answer`), and one trigger cannot carry that. Per BLANK the spectrum disappears:
 * a single blank is either typed into or it is not.
 *
 * Rides the existing word-trigger format rather than inventing a second one, so every parser,
 * picker and chip already reaches it and only has to recognise the sentinel.
 */
export const FITB_BLANK_EMPTY = '__empty__';
/** Returns null if `trigger` isn't a per-word FITB trigger. Ids are crypto.randomUUID() strings
 * (hex + hyphens only), so splitting on the literal infix is unambiguous. */
export function parseFitbWordTrigger(trigger) {
    if (!trigger.startsWith('blank_'))
        return null;
    const rest = trigger.slice('blank_'.length);
    const idx = rest.indexOf(TRIGGER_INFIX);
    if (idx === -1)
        return null;
    return { blankId: rest.slice(0, idx), wordId: rest.slice(idx + TRIGGER_INFIX.length) };
}
/**
 * Generalizes Dropdown's `resolveEffectiveOptions` across the blank → word nesting. Every
 * `FitbBlankWord.id` is already a globally-unique `crypto.randomUUID()` across the whole block (not
 * just within its own blank), so a branch's flat `Record<id, role>` map works unmodified here — the
 * "matrix" (blanks × words) is a UI grouping concern only, never a new data shape.
 *
 * Same fail-closed contract as `resolveEffectiveOptions`: on no branch match (missing/invalid
 * equation, or a coverage gap), every word in every blank becomes `'neutral'` rather than falling
 * back to each word's own static role — an incomplete/unresolvable branch setup must be
 * unanswerable-correctly, not silently reuse a still-`'correct'` word nobody reviewed for this
 * specific branch.
 */
/**
 * A follower's effective blanks for the scenario in force — the scenario twin of
 * `resolveEffectiveBlanks`, written beside it on purpose.
 *
 * The fail-closed rule (unlinked returns unchanged, a missing row goes ALL NEUTRAL) lives once
 * in `resolveScenarioRoles`. What is passed from here is only FITB's own shape: what neutral
 * means when roles are nested inside blanks, and the flat word-id merge.
 */
export function resolveScenarioBlanks(blanks, branches, scenarioId, 
/** For a row that branches on a comparison of its own — see `rolesForScenarioRow`. */
resolvedVariables = {}, angleMode = 'degrees') {
    return resolveScenarioRoles(blanks, branches, scenarioId, bs => bs.map(b => ({ ...b, words: b.words.map(w => ({ ...w, role: 'neutral' })) })), (bs, roles) => bs.map(b => ({ ...b, words: versionRoles(b.words, roles) })), resolvedVariables, angleMode);
}
export function resolveEffectiveBlanks(blanks, equation, branches, resolvedVariables, angleMode = 'degrees') {
    if (!branches || branches.length === 0)
        return blanks;
    const matched = resolveMatchedBranch(equation, branches, resolvedVariables, angleMode);
    if (!matched)
        return blanks.map(b => ({ ...b, words: b.words.map(w => ({ ...w, role: 'neutral' })) }));
    // The SAME merge a version applies — see `applyFitbVersion` for why a flat word-id key works
    // across nested blanks.
    return blanks.map(b => ({ ...b, words: versionRoles(b.words, matched.roles) }));
}
/**
 * Ids of every blank that lacks a correct-role word *within this branch's own role map* — FITB's
 * correctness is AND-across-blanks (see `evaluateFitb` below), so a branch's completeness is a
 * per-blank question, unlike Keyword's single `'none-correct'` signal (Keyword has only one answer
 * field, never several independent ones that all need their own correct entry). A branch can be
 * correctly configured for some blanks and not others; this surfaces exactly which ones are still
 * missing rather than one all-or-nothing boolean. Empty array = this branch is fully configured.
 */
export function fitbBranchMissingBlanks(branch, blanks) {
    return blanks.filter(b => !b.words.some(w => branch.roles[w.id] === 'correct')).map(b => b.id);
}
/**
 * Returns the feedback states that match a student's submitted blank values.
 *
 * Evaluation sequence (same shape as evaluateKeyword, with an incomplete-answer structural gate
 * mirroring evaluateNumerical's withUnits mode):
 * 1. optional mode → any_response, answered or not — no completeness or correctness check at
 *    all for this mode (see positiveTriggerForMode in use-input-block-feedback.ts).
 * 2. Every blank empty (graded/survey only) → unanswered state.
 * 3. At least one blank empty, not all → incomplete_answer. Purely about attempt-completeness —
 *    doesn't matter whether the *filled* blanks are correct, neutral, or wrong; a single
 *    untouched blank alongside otherwise-correct ones is still Incomplete, checked before
 *    correctness is ever assessed. A submission with nothing empty (even if some blanks are
 *    neutral or wrong) never hits this branch — it falls through to graded classification below.
 * 4. survey mode (only reached once no blank is empty) → any_answer.
 * 5. graded mode, no blanks empty: each blank's submitted text is normalized and matched against
 *    that blank's own word list (unmatched text is treated like Keyword's unmatched answer).
 *    If every blank's word is role 'correct' → `correct`, terminal (mirrors Keyword's
 *    correct-first-terminal rule, generalized across N blanks). Otherwise every blank's
 *    non-correct word fires its own targeted feedback in parallel (never sequential/first-match,
 *    matching every other block's convention), falling back to `incorrect` if nothing fired.
 *
 * `resolvedVariables` resolves `conditionalBranches` — optional, and a no-op whenever
 * `blockData.conditionalBranches` is unset. Branches are consulted ONLY in `graded` mode, same gate
 * as `evaluateDropdown`/`evaluateMCQ` — every branch's own feedback only ever contains
 * graded-shaped triggers (unanswered/incomplete_answer/correct/incorrect/targeted), never
 * `any_answer`/`any_response`, and switching `gradingMode` away from `'graded'` deliberately does
 * NOT clear branch data. When a branch resolves, its OWN feedback fires for *every* trigger below
 * (unanswered through incorrect) — not the block's top-level `feedback` — since a branch
 * fundamentally changes which words are correct, its messaging (including "no answer"/"incomplete")
 * has to be able to differ too, for the exact same consistency reason it already does on
 * Dropdown/Keyword.
 */
export function evaluateFitb(answer, blockData, resolvedVariables = {}, 
/** The leader's variant in force for this attempt — see `evaluateDropdown`'s own note. */
scenarioId) {
    const { values } = answer;
    const { caseSensitive, accentSensitive, gradingMode, feedback, conditionalEquation, conditionalBranches, scenarioBranches, angleMode } = blockData;
    /* A FOLLOWER'S SCENARIO ROW REPLACES the BLOCK's numeric branch, never joins it. The row's
       OWN nested branching is a different thing — see `rolesForScenarioRow`. */
    const followed = gradingMode === 'graded' && !!scenarioBranches && scenarioBranches.length > 0;
    const scenarioRow = followed ? pickScenarioBranch(scenarioBranches, scenarioId) : null;
    const matchedBranch = !followed && gradingMode === 'graded'
        ? resolveMatchedBranch(conditionalEquation, conditionalBranches, resolvedVariables, angleMode)
        : null;
    const blanks = gradingMode !== 'graded' ? blockData.blanks
        : followed ? resolveScenarioBlanks(blockData.blanks, scenarioBranches, scenarioId)
            : resolveEffectiveBlanks(blockData.blanks, conditionalEquation, conditionalBranches, resolvedVariables, angleMode);
    // Optional chaining, not a plain ternary — a branch saved before `feedback` was added to
    // ConditionalRoleBranch has no such field at all; falling back to the block's own top-level
    // feedback here is safer than crashing on `.states` of `undefined`.
    // Incomplete branch coverage falls back to the top-level set with targeted states STRIPPED, so an
    // unmatched answer always lands on Default Incorrect instead of a leftover targeted state firing
    // terminally on an option that was just declared neutral. See activeFeedbackForBranch.
    const activeFeedback = activeFeedbackForBranch((scenarioRow ?? matchedBranch)?.feedback, feedback, gradingMode === 'graded'
        && (followed || (!!conditionalBranches && conditionalBranches.length > 0)));
    const filled = (id) => (values[id] ?? '').trim() !== '';
    /**
     * Which authored word each blank matched, and the targeted states for them. ONE matcher, used
     * by every grading mode — see `participationOutcome`.
     *
     * `withCorrectness` drops the `role === 'correct'` skip, which exists in graded so a targeted
     * state can never override a correct answer. Outside graded no role is consulted at all.
     */
    // The SAME matcher Keyword grades with — a blank is a keyword question with a shorter list,
    // and two implementations of "did they say oxygen" is two answers to one question.
    const matchesFor = () => blanks.map(blank => ({
        blank,
        word: matchedKeywordOption(values[blank.id] ?? '', blank.words, caseSensitive, accentSensitive),
    }));
    const targetedFor = (withCorrectness) => matchesFor().flatMap(({ blank, word }) => {
        if (!word || (withCorrectness && word.role === 'correct'))
            return [];
        const st = activeFeedback.states.find(x => x.type === 'targeted' && x.trigger === buildFitbWordTrigger(blank.id, word.id));
        return st ? [st] : [];
    });
    if (gradingMode === 'optional') {
        // OPTIONAL is INERT: the catch-all, whatever the student did. See `targetedFiresIn`.
        return optionalOutcome(activeFeedback.states);
    }
    if (blanks.every(b => !filled(b.id))) {
        const unanswered = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'unanswered');
        return unanswered ? [unanswered] : [];
    }
    if (blanks.some(b => !filled(b.id))) {
        const incomplete = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'incomplete_answer');
        return incomplete ? [incomplete] : [];
    }
    if (gradingMode === 'survey') {
        return participationOutcome(activeFeedback.states, 'survey', targetedFor(false));
    }
    const matches = matchesFor();
    if (matches.every(m => m.word?.role === 'correct')) {
        const correct = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'correct');
        return correct ? [correct] : [];
    }
    const matched = targetedFor(true);
    if (matched.length > 0)
        return matched;
    const incorrect = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'incorrect');
    return incorrect ? [incorrect] : [];
}
/**
 * A FITB block resolved to one of its versions.
 *
 * The words are two levels down, but the KEY is flat — every `FitbBlankWord.id` is unique across
 * the whole block — so this walks the blanks and hands each one's words to the same shared merge
 * every other block uses. Nothing about the merge is FITB-specific.
 */
export function applyFitbVersion(data, versionId) {
    const version = pickVersion(data.versions, versionId);
    if (!version)
        return data;
    return {
        ...applyVersionHead(data, version),
        blanks: data.blanks.map(b => ({ ...b, words: versionRoles(b.words, version.roles) })),
    };
}
/** Every word's role, flattened back out of the blanks — see `applyFitbVersion`. */
function fitbRoles(data) {
    return Object.fromEntries(data.blanks.flatMap(b => b.words.map(w => [w.id, w.role])));
}
export function writeFitbVersion(current, versionId, next) {
    return writeVersionWithRoles(current, versionId, next, fitbRoles(next));
}
export function addFitbVersion(data, fromVersionId, newId) {
    return addVersionWithRoles(data, fromVersionId, newId, fitbRoles(data));
}
export function removeFitbVersion(data, versionId) {
    return removeVersionWith(data, versionId, applyFitbVersion);
}
//# sourceMappingURL=fitb.js.map